import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { Menu } from '@headlessui/react';
import {
  EllipsisVerticalIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';
import { TbFilterPause } from "react-icons/tb";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getDeviceInfo } from '../utils/deviceInfo';
import { motion, AnimatePresence } from 'framer-motion';
import { Switch } from '@headlessui/react';

const API_URL = process.env.REACT_APP_API_URL;

const Banners = ({ children }) => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const navigate = useNavigate();
  const [access_data, setAccess_data] = useState([]);

  // Form state for adding new banner
  const [formData, setFormData] = useState({
    image: null,
    redirect_url: '',
    status: 'Active'
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (sessionCheck() == null) {
      navigate("../login", { replace: true });
    } else {
      setAccess_data(JSON.parse(sessionCheck()));
    }
  }, []);

  useEffect(() => {
    if (access_data?.username) {
      fetchBanners();
    }
  }, [access_data?.username]);

  useEffect(() => {
    if (filterDrawerOpen || deleteModalOpen || addModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [filterDrawerOpen, deleteModalOpen, addModalOpen]);

  const handleStatusChange = async (bannerId, newActiveStatus) => {
    const newStatus = newActiveStatus ? 'Active' : 'Inactive';

    setBanners(prevBanners =>
      prevBanners.map(banner =>
        banner.id === bannerId ? { ...banner, status: newStatus } : banner
      )
    );

    try {
      const response = await updateBannerStatus(bannerId, newStatus);
      if (!response) throw new Error('Update failed');

      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      setBanners(prevBanners =>
        prevBanners.map(banner =>
          banner.id === bannerId ? { ...banner, status: banner.status === 'Active' ? 'Inactive' : 'Active' } : banner
        )
      );
      toast.error(error.message);
    }
  };

  const updateBannerStatus = async (bannerId, newStatus) => {
    try {
      const dataToEncrypt = {
        user_id: access_data?.username || '',
        login_id: access_data?.login_id || '',
        device_id: getDeviceInfo().deviceId,
        payload: {
          query: 'status-update',
          bannerId: bannerId,
          newStatus: newStatus
        }
      };

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);
      if (!encryptedMessage) {
        throw new Error("Encryption failed");
      }

      const response = await fetch(`${API_URL}banner-operation.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify({
          encrypted_key: encryptedMessage.encryptedKey,
          encrypted_data: encryptedMessage.encryptedData,
        }),
      });

      const result = await response.json();
      return result.status === true;
    } catch (error) {
      console.error('Status update error:', error);
      throw error;
    }
  };

  const handleDeleteBanner = async () => {
    if (!selectedBanner) return;

    try {
      const response = await deleteBanner(selectedBanner.id);
      if (!response) throw new Error('Delete failed');

      setBanners(prev => prev.filter(banner => banner.id !== selectedBanner.id));

      setDeleteModalOpen(false);
      setSelectedBanner(null);
      toast.success('Banner deleted successfully');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const deleteBanner = async (bannerId) => {
    try {
      const dataToEncrypt = {
        user_id: access_data?.username || '',
        login_id: access_data?.login_id || '',
        device_id: getDeviceInfo().deviceId,
        payload: {
          query: 'delete-banner',
          bannerId: bannerId
        }
      };

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);
      if (!encryptedMessage) {
        throw new Error("Encryption failed");
      }

      const response = await fetch(`${API_URL}banner-operation.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify({
          encrypted_key: encryptedMessage.encryptedKey,
          encrypted_data: encryptedMessage.encryptedData,
        }),
      });

      const result = await response.json();
      return result.status === true;
    } catch (error) {
      console.error('Delete error:', error);
      throw error;
    }
  };

  const handleAddBanner = async () => {
    if (!formData.image) {
      toast.error('Please select an image');
      return;
    }

    setUploading(true);
    try {
      const response = await addBanner(formData);
      if (!response) throw new Error('Add banner failed');

      setAddModalOpen(false);
      setFormData({
        image: null,
        redirect_url: '',
        status: 'Active'
      });

      // Refresh the banner list
      fetchBanners();
      toast.success('Banner added successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const addBanner = async (bannerData) => {
    try {
      const formData = new FormData();
      formData.append('image', bannerData.image);
      formData.append('redirect_url', bannerData.redirect_url);
      formData.append('status', bannerData.status === 'Active' ? '1' : '0');

      // Encrypt other data if needed
      const dataToEncrypt = {
        user_id: access_data?.username || '',
        login_id: access_data?.login_id || '',
        device_id: getDeviceInfo().deviceId,
        payload: {
          query: 'add-banner',
          redirect_url: bannerData.redirect_url,
          status: bannerData.status === 'Active' ? '1' : '0'
        }
      };

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);
      if (!encryptedMessage) {
        throw new Error("Encryption failed");
      }

      formData.append('encrypted_key', encryptedMessage.encryptedKey);
      formData.append('encrypted_data', encryptedMessage.encryptedData);

      const response = await fetch(`${API_URL}banner-operation.php`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: formData,
      });

      const result = await response.json();
      return result.status === true;
    } catch (error) {
      console.error('Add banner error:', error);
      throw error;
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type and size
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('Image size should be less than 5MB');
        return;
      }
      setFormData(prev => ({ ...prev, image: file }));
    }
  };

  const fetchBanners = async (filterParams = null) => {
    setLoading(true);

    try {

      const dataToEncrypt = {
        user_id: access_data?.username || '',
        login_id: access_data?.login_id || '',
        device_id: getDeviceInfo().deviceId,
        payload: {
          query: 'fetch-banner',
        }
      };

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);

      if (!encryptedMessage) {
        toast.error("Encryption failed. Please try again.");
        setLoading(false);
        return;
      }

      const payloadToSend = {
        encrypted_key: encryptedMessage.encryptedKey,
        encrypted_data: encryptedMessage.encryptedData,
      };

      const response = await fetch(`${API_URL}banner-operation.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify(payloadToSend),
      });

      const result = await response.json();

      if (result.status === true) {
        const apiBanners = result.data.map(item => ({
          id: item.id,
          url: item.url || 'N/A',
          redirect_url: item.redirect_url || 'N/A',
          status: item.status === "1" ? 'Active' : 'Inactive',
          createdAt: item.createAt || 'N/A',
        }));

        setBanners(apiBanners);
      } else {
        throw new Error(result.message || "Failed to fetch banner data.");
      }
    } catch (error) {
      setBanners([]);
      console.error("Fetch banner error:", error);
      toast.error(error.message || "An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
      <div className="flex flex-col flex-1">
        <Header />
        <main className="flex-1 overflow-auto">
          {/* Delete Confirmation Modal */}
          <AnimatePresence>
            {deleteModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6"
                >
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Confirm Deletion
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-6">
                    Are you sure you want to delete this banner? This action cannot be undone.
                  </p>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setDeleteModalOpen(false);
                        setSelectedBanner(null);
                      }}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteBanner}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>

          {/* Add Banner Modal */}
          <AnimatePresence>
            {addModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      Add New Banner
                    </h3>
                    <button
                      onClick={() => setAddModalOpen(false)}
                      className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Banner Image
                      </label>
                      <div className="flex items-center justify-center w-full">
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500">
                          {formData.image ? (
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <EyeIcon className="w-8 h-8 mb-2 text-gray-500" />
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {formData.image.name}
                              </p>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <ArrowUpTrayIcon className="w-8 h-8 mb-2 text-gray-500" />
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                Click to upload image
                              </p>
                            </div>
                          )}
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={handleImageChange}
                          />
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Redirect URL (Optional)
                      </label>
                      <input
                        type="url"
                        value={formData.redirect_url}
                        onChange={(e) => setFormData(prev => ({ ...prev, redirect_url: e.target.value }))}
                        placeholder="https://example.com"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700"
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-6 flex space-x-3">
                    <button
                      onClick={() => setAddModalOpen(false)}
                      className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddBanner}
                      disabled={uploading}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {uploading ? 'Uploading...' : 'Add Banner'}
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>

          {/* Overlay when modals are open */}
          {(filterDrawerOpen || deleteModalOpen || addModalOpen) && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => {
                if (filterDrawerOpen) setFilterDrawerOpen(false);
                if (deleteModalOpen) {
                  setDeleteModalOpen(false);
                  setSelectedBanner(null);
                }
                if (addModalOpen) setAddModalOpen(false);
              }}
            />
          )}

          <main className="flex-1 p-6 overflow-auto">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                >
                  <ChevronLeftIcon className="h-5 w-5 mr-1" />
                  Back
                </button>
                <h2 className="text-xl font-semibold">
                  Banner Management
                </h2>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setAddModalOpen(true)}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <PlusIcon className="h-5 w-5 mr-1" />
                    Add
                  </button>
                </div>
              </div>

              {children || (
                <>
                  {/* Banner Cards */}
                  <div className="space-y-4">
                    {loading && (
                      <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                      </div>
                    )}

                    {!loading && banners.length > 0 ? (
                      <>
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                          {/* Banner Table */}
                          <div className="overflow-x-auto rounded-lg shadow">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead className="bg-gray-100 dark:bg-gray-800">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">ID</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Preview</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Redirect URL</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Created At</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Status</th>
                                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {banners.map((banner) => (
                                  <tr key={banner.id} className="bg-white dark:bg-gray-700">
                                    {/* ID */}
                                    <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                                      #{banner.id}
                                    </td>

                                    {/* Preview */}
                                    <td className="px-3 py-2">
                                      <img
                                        src={banner.url}
                                        alt="Banner"
                                        className="h-12 w-20 object-cover rounded-md"
                                        onError={(e) => {
                                          e.target.src =
                                            "https://via.placeholder.com/96x64?text=Image+Error";
                                        }}
                                      />
                                    </td>

                                    {/* Redirect URL */}
                                    <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300 max-w-[200px] truncate">
                                      {banner.redirect_url ? (
                                        <a
                                          href={banner.redirect_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-600 dark:text-blue-400 hover:underline"
                                        >
                                          {banner.redirect_url}
                                        </a>
                                      ) : (
                                        <span className="text-gray-400">No redirect</span>
                                      )}
                                    </td>

                                    {/* Created At */}
                                    <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                                      {banner.createdAt}
                                    </td>

                                    {/* Status */}
                                    <td className="px-3 py-2">
                                      <div className="flex items-center">
                                        <Switch
                                          checked={banner.status === "Active"}
                                          onChange={(enabled) => handleStatusChange(banner.id, enabled)}
                                          className={`${banner.status === "Active" ? "bg-green-500" : "bg-red-500"
                                            } relative inline-flex h-5 w-9 items-center rounded-full`}
                                        >
                                          <span className="sr-only">Toggle status</span>
                                          <span
                                            className={`${banner.status === "Active"
                                                ? "translate-x-4"
                                                : "translate-x-1"
                                              } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                                          />
                                        </Switch>
                                      </div>
                                    </td>

                                    {/* Actions */}
                                    <td className="px-3 py-2 text-center">
                                      <div className="flex justify-center gap-2">
                                        {/* View Image */}
                                        <a
                                          href={banner.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                                          title="View Image"
                                        >
                                          <EyeIcon className="h-5 w-5 text-blue-500" />
                                        </a>

                                        {/* Delete */}
                                        <button
                                          onClick={() => {
                                            setSelectedBanner(banner);
                                            setDeleteModalOpen(true);
                                          }}
                                          className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                                          title="Delete"
                                        >
                                          <TrashIcon className="h-5 w-5 text-red-500" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    ) : (
                      !loading && (
                        <div className="text-center py-12">
                          <p className="text-gray-500 dark:text-gray-400">No banners found</p>
                          <button
                            onClick={() => setAddModalOpen(true)}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                          >
                            Add Your First Banner
                          </button>
                        </div>
                      )
                    )}
                  </div>
                </>
              )}
            </div>
          </main>
        </main>
      </div>
      <ToastContainer />
    </div>
  );
};

export default Banners;